// IL PANNELLO TRASFERIMENTI (2026-09-13).
//
// Prende il posto della coda dei soli caricamenti: una lista sola, con dentro
// caricamenti E scaricamenti, i gruppi delle cartelle, e in fondo una riga di
// totale. Il motore non cambia — un file alla volta in salita, quattro blocchi
// dentro il file — cambia quello che si vede.
//
// Questo componente DISEGNA soltanto: riceve le righe e sei funzioni, non
// chiama il server e non decide niente. Chi comanda resta VaultPage.
//
// LA CARTELLA. Prima non compariva da nessuna parte: si sceglieva una cartella
// con dentro trecento file e l'unico segno era la vecchia barra che saliva e
// scendeva trecento volte. Adesso i file entrano in coda come tutti gli altri,
// con un gruppoId comune, e in cima al gruppo c'e' una riga che dice nome,
// quanti file e a che punto e' l'insieme.
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { velocitaLeggibile, tempoLeggibile } from '../utils/velocita';
import { seguiBarra } from '../utils/barreFisse';
import './Trasferimenti.css';
import { unitaByte } from '../utils/unita';

function peso(byte, lingua) {
  if (byte == null) return '';
  const virgola = lingua === 'en' ? '.' : ',';
  const mb = byte / (1024 * 1024);
  if (mb < 1) return `${Math.max(1, Math.round(byte / 1024))} ${unitaByte(lingua, 'kB')}`;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0).replace('.', virgola)} ${unitaByte(lingua, 'MB')}`;
  return `${(mb / 1024).toFixed(2).replace('.', virgola)} ${unitaByte(lingua, 'GB')}`;
}

const IN_CORSO = new Set(['attesa', 'in-corso', 'in-pausa', 'chiede']);

export default function Trasferimenti({
  righe, gruppi, totale,
  onRiprova, onAnnulla, onPausa, onRiprendi, onScegli, onChiudi, onTogliRiga,
}) {
  const { t, i18n } = useTranslation('vault');
  const [aperto, setAperto] = useState(true);
  const mio = useRef(null);
  const lingua = i18n.language;

  // La pagina scende di quanto e' alto il pannello — o della sola pastiglia
  // quando e' chiuso. Misurato dal vivo: l'altezza cambia col numero di righe,
  // con la lingua e quando si chiude.
  useEffect(() => seguiBarra('trasferimenti', mio.current), [righe.length, aperto, lingua]);

  if (!righe.length) return null;

  const attivi = righe.filter((r) => IN_CORSO.has(r.stato)).length;
  const fatti = righe.filter((r) => r.stato === 'fatto').length;
  const falliti = righe.filter((r) => r.stato === 'fallito').length;
  // L'avanzamento complessivo, in byte: e' quello che va nella testata.
  const percentoTotale = totale && totale.byteTotali > 0
    ? Math.max(0, Math.min(100, Math.round((totale.byteFatti / totale.byteTotali) * 100))) : 0;

  // ── LA PASTIGLIA ────────────────────────────────────────────────────────
  // Quando il pannello e' chiuso non deve sparire il fatto che qualcosa sta
  // succedendo: resta un segno, attaccato in alto, che dice quanti.
  if (!aperto) {
    return (
      <div className="tr-pastiglia-barra" ref={mio}>
        <button type="button" className="tr-pastiglia" onClick={() => setAperto(true)}>
          <span className="tr-pastiglia-punto" aria-hidden="true" />
          <span className="tr-pastiglia-nome">{t('tr_nome')}</span>
          <span className="tr-riep-sep"> · </span>
          {attivi > 0
            ? t('tr_pastiglia_attivi', { count: attivi })
            : t('tr_pastiglia_fermi', { count: fatti })}
          {falliti > 0 && <span className="tr-pastiglia-falliti"> · {t('tr_riep_falliti', { count: falliti })}</span>}
        </button>
      </div>
    );
  }

  const rigaDiGruppo = (g) => {
    const suoi = righe.filter((r) => r.gruppoId === g.id);
    const finiti = suoi.filter((r) => r.stato === 'fatto').length;
    const pc = g.byteTotali > 0 ? Math.round((g.byteFatti / g.byteTotali) * 100) : 0;
    return (
      <li key={'g' + g.id} className="tr-riga tr-gruppo">
        <span className="tr-icona" aria-hidden="true">📁</span>
        <span className="tr-nome" title={g.nome}>{g.nome}</span>
        <span className="tr-peso">{t('tr_gruppo_conta', { fatti: finiti, totale: suoi.length })}</span>
        <span className="tr-stato">{pc}%</span>
        <span className="tr-azioni" />
        <span className="tr-pista"><span className="tr-riempi" style={{ width: `${pc}%` }} /></span>
      </li>
    );
  };

  // Le righe si mostrano nell'ordine in cui sono entrate, con la riga di
  // gruppo appena prima del suo primo file: cosi' una cartella resta un
  // blocco unico invece di sparpagliarsi.
  const elenco = [];
  const gruppiVisti = new Set();
  for (const r of righe) {
    if (r.gruppoId && !gruppiVisti.has(r.gruppoId)) {
      gruppiVisti.add(r.gruppoId);
      const g = gruppi.find((x) => x.id === r.gruppoId);
      if (g) elenco.push(rigaDiGruppo(g));
    }
    elenco.push(
      <li key={r.id} className={`tr-riga tr-${r.stato} ${r.gruppoId ? 'tr-figlio' : ''}`}>
        {/* IN SU non una freccia, ma quello che succede davvero e che nessun
            altro gestore di file fa: il file si chiude a chiave sul dispositivo
            prima di partire. Due SVG sovrapposti (documento, lucchetto) animati
            SOLO in CSS — opacita' e arco che scende — con zero lavoro
            JavaScript per fotogramma (vedi Trasferimenti.css, .tr-icona-cifra).
            Su "fatto" il lucchetto resta chiuso e verde. IN GIU' resta ↓. */}
        <span className={`tr-icona${r.verso === 'giu' ? '' : ' tr-icona-cifra'}`} aria-hidden="true">
          {r.verso === 'giu' ? '↓' : (
            <>
              <svg className="tr-doc" viewBox="0 0 14 14" focusable="false"><path d="M3 1.2h5.2l3 3v8.6H3z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M8.2 1.2v3h3" fill="none" stroke="currentColor" strokeWidth="1.3"/></svg>
              <svg className="tr-luc" viewBox="0 0 14 14" focusable="false"><rect x="2.6" y="6.2" width="8.8" height="6.6" rx="1.4" fill="currentColor"/><path className="tr-arco" d="M4.6 6.2V4.3a2.4 2.4 0 0 1 4.8 0v1.9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </>
          )}
        </span>
        <span className="tr-nome" title={r.nome}>{r.nome}</span>
        <span className="tr-peso">{peso(r.byteTotali, lingua)}</span>

        <span className="tr-stato">
          {r.stato === 'attesa' && t('coda_attesa')}
          {r.stato === 'in-pausa' && t('tr_in_pausa')}
          {r.stato === 'fatto' && t('coda_fatto')}
          {r.stato === 'annullato' && t('coda_annullato')}
          {r.stato === 'nonCaricato' && t('coda_non_caricato')}
          {/* NON E' UN ERRORE, E' UN'OFFERTA (2026-09-18). Prima qui c'era
              solo il conto dei pezzi gia' arrivati: un utente l'ha letto come
              un guasto da doppio caricamento e ha rinunciato a caricare. Ora
              la riga fa una domanda, e il conto sta sotto, piu' piccolo, come
              informazione di contorno. I due bottoni fanno esattamente quello
              che facevano prima. */}
          {r.stato === 'chiede' && (
            <span className="tr-ripresa">
              <span className="tr-ripresa-domanda">{t('coda_chiede_domanda')}</span>
              <span className="tr-ripresa-parti">{t('coda_chiede_parti', { fatti: r.sessione?.arrivati, totale: r.sessione?.totaleBlocchi })}</span>
            </span>
          )}
          {r.stato === 'fallito' && <span className="tr-motivo">{r.motivo}</span>}
          {r.stato === 'in-corso' && r.fase === 'assemblaggio' && <span className="tr-vivo">{t('tr_assemblaggio')}</span>}
          {r.stato === 'in-corso' && r.fase === 'coda' && <span className="tr-vivo">{r.posizione > 0 ? t('tr_in_coda', { n: r.posizione }) : t('tr_in_coda_prossimo')}</span>}
          {r.stato === 'in-corso' && r.fase === 'attesa-spazio' && <span className="tr-vivo">{t('tr_in_attesa_spazio')}</span>}
          {r.stato === 'in-corso' && !['assemblaggio', 'coda', 'attesa-spazio'].includes(r.fase) && r.percento == null && t('tr_in_corso')}
          {r.stato === 'in-corso' && !['assemblaggio', 'coda', 'attesa-spazio'].includes(r.fase) && r.percento != null && (
            <span className="tr-vivo">
              {/* Accanto alla percentuale, in su, la parola che dice cosa sta
                  succedendo: il file viene cifrato mentre parte. */}
              {r.verso !== 'giu' && <span className="tr-cifratura">{t('tr_cifratura')} </span>}
              <strong>{r.percento}%</strong>
              {r.velocita != null && <span className="tr-vel"> · {velocitaLeggibile(r.velocita, lingua)}</span>}
              {r.restano != null && <span className="tr-eta"> · {t('tr_restano', { tempo: tempoLeggibile(r.restano, lingua) })}</span>}
            </span>
          )}
        </span>

        <span className="tr-azioni">
          {r.stato === 'chiede' && (
            <>
              <button className="tr-btn tr-btn-forte" onClick={() => onScegli(r.id, 'riprendi')}>{t('ripresa_riprendi')}</button>
              <button className="tr-btn" onClick={() => onScegli(r.id, 'ricomincia')}>{t('ripresa_ricomincia')}</button>
            </>
          )}
          {r.stato === 'in-corso' && (
            <>
              {/* La pausa c'e' solo dove esiste davvero: sulla strada a
                  blocchi si smette di mandare pezzi e la sessione resta
                  aperta. In discesa, e sulla strada vecchia, "pausa"
                  vorrebbe dire interrompere e ricominciare — non e' una
                  pausa, e non la si finge. */}
              {r.pausabile && <button className="tr-btn" onClick={() => onPausa(r.id)}>{t('tr_pausa')}</button>}
              <button className="tr-btn" onClick={() => onAnnulla(r.id)}>{t('coda_annulla')}</button>
            </>
          )}
          {r.stato === 'in-pausa' && (
            <>
              <button className="tr-btn tr-btn-forte" onClick={() => onRiprendi(r.id)}>{t('tr_riprendi')}</button>
              <button className="tr-btn" onClick={() => onAnnulla(r.id)}>{t('coda_annulla')}</button>
            </>
          )}
          {r.stato === 'fallito' && (
            <>
              {r.riprendibile && (
                <span className="tr-riprende">
                  {r.bloccoDa > 0 ? t('coda_riprende_da', { fatti: r.bloccoDa }) : t('coda_riprende')}
                </span>
              )}
              <button className="tr-btn tr-btn-forte" onClick={() => onRiprova(r.id)}>{t('coda_riprova')}</button>
              <button className="tr-btn tr-btn-muto" onClick={() => onTogliRiga(r.id)} aria-label={t('coda_togli')}>✕</button>
            </>
          )}
        </span>

        {(r.stato === 'in-corso' || r.stato === 'in-pausa') && r.percento != null && (
          <span className="tr-pista"><span className="tr-riempi" style={{ width: `${r.percento}%` }} /></span>
        )}
      </li>,
    );
  }

  return (
    <div className="tr-pannello" ref={mio}>
      {/* LA TESTATA (2026-09-14). Prima c'era solo il riepilogo, da solo:
          "0 completati · 1 non riuscito" senza un nome sopra sembrava un
          avviso di sistema. Adesso il pannello si presenta: icona su/giu' e
          "Trasferimenti" a sinistra, il riepilogo a destra, e quando c'e'
          qualcosa in corso la barra di avanzamento complessiva sotto — cosi'
          si capisce a colpo d'occhio che quello e' il posto dove guardare.
          Il rosso NON sta qui: una riga andata male colora la sua riga, non
          la testata e non il pannello. */}
      <button className="tr-testa" onClick={() => setAperto(false)} aria-expanded="true">
        <span className="tr-testa-sx">
          <span className="tr-testa-icona" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13V3M5 3 2.5 5.5M5 3l2.5 2.5" /><path d="M11 3v10m0 0 2.5-2.5M11 13l-2.5-2.5" />
            </svg>
          </span>
          <span className="tr-testa-nome">{t('tr_nome')}</span>
        </span>
        <span className="tr-testa-destra">
          <span className="tr-riepilogo">
            {attivi > 0 && <span>{t('tr_riep_attivi', { count: attivi })}</span>}
            {attivi > 0 && <span className="tr-riep-sep"> · </span>}
            <span>{t('tr_riep_completati', { count: fatti })}</span>
            {falliti > 0 && <span className="tr-riep-sep"> · </span>}
            {falliti > 0 && <span>{t('tr_riep_falliti', { count: falliti })}</span>}
          </span>
          <span className="tr-freccia" aria-hidden="true">▾</span>
          {attivi === 0 && (
            <span
              className="tr-chiudi" role="button" tabIndex={0}
              aria-label={t('coda_chiudi')}
              onClick={(e) => { e.stopPropagation(); onChiudi(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChiudi(); } }}
            >✕</span>
          )}
        </span>
      </button>
      {attivi > 0 && (
        <div className="tr-testa-pista" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={percentoTotale}>
          <span className="tr-testa-riempi" style={{ width: `${percentoTotale}%` }} />
        </div>
      )}

      <ul className="tr-righe">{elenco}</ul>

      {/* ── LA RIGA DI TOTALE ────────────────────────────────────────────
          Quanto manca in tutto, non file per file: e' la domanda vera di chi
          ha messo su una cartella da trecento foto e vuole sapere se puo'
          andare a cena. */}
      {attivi > 0 && (
        <div className="tr-totale">
          <span className="tr-totale-sx">{t('tr_totale_restano', { fatto: peso(totale.byteFatti, lingua), tutto: peso(totale.byteTotali, lingua) })}</span>
          <span className="tr-totale-dx">
            {totale.velocita != null && <span>{velocitaLeggibile(totale.velocita, lingua)}</span>}
            {totale.restano != null && <span className="tr-totale-eta"> · {t('tr_restano', { tempo: tempoLeggibile(totale.restano, lingua) })}</span>}
          </span>
        </div>
      )}
    </div>
  );
}
